import { Box, Card, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Products } from "../../../../../../merchant/admin/priceList/priceListTypes"
import { Tag } from "../../../../../../../shared/components/createOrder/components/shop/product/Tag"
import { OfferTag } from "../../../../../../../shared/components/createOrder/components/shop/product/OfferTag"
import { OnlyXLeftTag } from "../../../../../../../shared/components/createOrder/components/shop/product/OnlyXLeftTag"
import { AvailableFromTag } from "../../../../../../../shared/components/createOrder/components/shop/product/AvailableFromTag"
import { ProductDisabledCover } from "../../../../../../../shared/components/productAvailability/ProductDisabledCover"
import { getProductLeftCount } from "../../../../../../../shared/components/productAvailability/stockHelpers"
import { faBurgerGlass } from "@fortawesome/pro-solid-svg-icons"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { CardImage } from "../../CardImage"
import { ProductOfferAmount } from "./ProductOfferAmount"
import { useState } from "react"
import { ProductDetailsDialog } from "../../../../../../../shared/components/createOrder/components/shop/product/ProductDetailsDialog"

const useStyles = makeStyles()((theme) => ({
    card: {
        boxShadow: "none",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
    },
    imageWrapper: {
        position: "relative",
        borderRadius: ".25rem",
        overflow: "hidden",
    },
    cardImage: {
        borderRadius: "0.25rem",
    },
    tagsRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.375rem",
        padding: ".375rem 0 0 0",
    },
    tagsFirstRow: {
        display: "flex",
        gap: "0.375rem",
        alignItems: "center",
        width: "100%",
    },
    marketingTagWrapper: {
        minWidth: 0,
        "& > div": {
            maxWidth: "100%",
        },
    },
    cardContent: {
        padding: ".5rem 0 0 0",
    },
    cardContentEmpty: {
        padding: ".5rem",
        fontWeight: 500,
        color: "#00000060",
    },
    title: {
        fontWeight: 900,
        fontSize: "0.938rem",
        lineHeight: "1.2",
        marginBottom: "0.25rem",
        color: theme.palette.tailwind.slate[700],
    },
    placeholder: {
        height: "7rem",
        color: "#94A3B8",
        display: "flex",
        borderRadius: ".25rem",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
}))

type ProductOfferProps = {
    product: Products
    userId?: number
}

export const ProductOffer = ({ userId, product }: ProductOfferProps) => {
    const { classes } = useStyles()
    const [productId, setProductId] = useState<number | null>(null)
    const showOfferTag = product.offerDetails
    const isUnavailable = product.orderDetails?.availability && !product.orderDetails.availability.isAvailable
    const onCardClick = () => setProductId(product.id)

    const productLeftInStock = getProductLeftCount(product?.stock) ?? product?.orderDetails?.productLeftInStock
    const availableFrom = product.offerDetails?.availabilityDetails?.dateTimeInterval?.from

    const hasTagBar = !!product?.productDescription?.marketingTag || !!productLeftInStock || !!availableFrom

    return (
        <>
            <Tooltip title={product?.description ?? ""} arrow>
                <Card className={classes.card}>
                    <Box className={classes.imageWrapper} onClick={isUnavailable ? undefined : onCardClick}>
                        <ProductDisabledCover availability={product.orderDetails?.availability} />
                        <CardImage imageUri={product?.imageUrl} icon={faBurgerGlass} className={classes.cardImage} />

                        {showOfferTag && (
                            <OfferTag top="0.5rem" right="0.5rem" offerDetails={product.offerDetails} size="sm" />
                        )}
                    </Box>
                    {hasTagBar && (
                        <Box className={classes.tagsRow}>
                            {product?.productDescription?.marketingTag ? (
                                <>
                                    <Box className={classes.tagsFirstRow}>
                                        <Box className={classes.marketingTagWrapper}>
                                            <Tag>{product.productDescription.marketingTag}</Tag>
                                        </Box>
                                        <OnlyXLeftTag amount={productLeftInStock} />
                                    </Box>
                                    <AvailableFromTag date={availableFrom} />
                                </>
                            ) : (
                                <>
                                    <OnlyXLeftTag amount={productLeftInStock} />
                                    <AvailableFromTag date={availableFrom} />
                                </>
                            )}
                        </Box>
                    )}
                    <Box className={classes.cardContent}>
                        <Typography
                            variant="subtitle2"
                            color="textPrimary"
                            className={classes.title}
                            onClick={onCardClick}
                        >
                            {product.name}
                        </Typography>
                        <ProductOfferAmount product={product} />
                    </Box>
                </Card>
            </Tooltip>
            {productId && userId && (
                <ProductDetailsDialog
                    userId={userId}
                    productId={productId}
                    handleClose={() => setProductId(null)}
                />
            )}
        </>
    )
}

export const ProductOfferEmpty = () => {
    const { classes } = useStyles()

    return (
        <Card className={classes.card}>
            <Box className={classes.placeholder}>
                <FontAwesomeIcon size="2x" icon={faBurgerGlass} />
            </Box>

            <Box className={classes.cardContentEmpty}>
                <LocalizedStrict id="user-home-empty-offers-text">
                    <Typography variant="subtitle2">You have no offers right now</Typography>
                </LocalizedStrict>
            </Box>
        </Card>
    )
}
